import React, { memo, useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';

import { createUseStyles, useTheme, Icon } from '@Sage/hoi-poi-ui';
import { getVar } from '@Sage/ssm-utils';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import { DEFAULT_CANVAS_HEIGHT } from 'hooks/useAudioEvents';

import styles from './styles';
const useStyles = createUseStyles(styles, 'AudioVisualizer');

/**
 * Format time in seconds to MM:SS format
 */
const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

/**
 * Action types for AudioVisualizer reducer
 */
const ACTIONS = {
    RESET_CURRENT_TIME: 'RESET_CURRENT_TIME',
    RESET_RECORDING_TIME: 'RESET_RECORDING_TIME',
    SET_CANVAS_WIDTH: 'SET_CANVAS_WIDTH',
    SET_CURRENT_TIME: 'SET_CURRENT_TIME',
    SET_RECORDING_TIME: 'SET_RECORDING_TIME',
};

/**
 * State reducer for AudioVisualizer
 */
const initialState = (width) => ({
    canvasWidth: width || 400,
    currentTime: 0,
    recordingTime: 0,
});

const visualizerReducer = (state, action) => {
    switch (action.type) {
        case ACTIONS.SET_CANVAS_WIDTH:
            return { ...state, canvasWidth: action.payload };
        case ACTIONS.SET_CURRENT_TIME:
            return { ...state, currentTime: action.payload };
        case ACTIONS.SET_RECORDING_TIME:
            return { ...state, recordingTime: action.payload };
        case ACTIONS.RESET_CURRENT_TIME:
            return { ...state, currentTime: 0 };
        case ACTIONS.RESET_RECORDING_TIME:
            return { ...state, recordingTime: 0 };
        default:
            return state;
    }
};

const AudioVisualizer = memo(
    ({
        audioBufferDuration,
        audioDuration = 0,
        bindCanvas,
        className = '',
        height = DEFAULT_CANVAS_HEIGHT,
        isActive = false,
        isPlaybackMode = false,
        isPlaying = false,
        level = 0,
        onPlayPause,
        onResize,
        onSeek,
        onTimeUpdate,
        playbackContext,
        playbackStartTime = 0,
        width,
    }) => {
        const classes = useStyles();
        const theme = useTheme();

        // ========================================
        // LOCAL STATE
        // ========================================
        const [state, dispatch] = useReducer(visualizerReducer, width, initialState);
        const { canvasWidth, currentTime, recordingTime } = state;
        const [isDragging, setIsDragging] = useState(false);
        const [dragPosition, setDragPosition] = useState(0);
        const wasPlayingRef = useRef(false);

        // ========================================
        // REFS
        // ========================================
        const playbackAnimationRef = useRef(null);
        const recordingAnimationRef = useRef(null);
        const canvasWrapperRef = useRef(null);
        const containerRef = useRef(null);
        const prevCanvasWidthRef = useRef(width || 400);
        const recordingStartTimeRef = useRef(null);
        const canvasRef = useRef(null);
        const scrubberAnimationRef = useRef(null);
        const prevScrubberPositionRef = useRef(null);
        const resizeObserverRef = useRef(null);

        // ========================================
        // DERIVED STATE & MEMOIZATION
        // ========================================
        // Use audioBufferDuration for playback timing if available (actual audio length)
        // Otherwise fall back to audioDuration (includes silences for recording)
        const playbackDuration = useMemo(
            () => (isPlaybackMode && audioBufferDuration ? audioBufferDuration : audioDuration),
            [audioBufferDuration, audioDuration, isPlaybackMode],
        );

        const displayTime = useMemo(() => {
            if (isPlaybackMode) {
                // In playback mode: show current time (whether playing or paused)
                // If at position 0, show duration instead
                if (currentTime === 0 && playbackDuration > 0) {
                    return playbackDuration;
                }
                return currentTime;
            }
            // During active recording: show wall-clock elapsed time (recordingTime)
            // This provides continuous visual feedback regardless of VAD/speech detection
            if (isActive) {
                return recordingTime;
            }
            // After recording stops (idle, not playback mode): show actual audio duration
            // audioDuration is calculated from actual audio batches, excluding silences
            return audioDuration > 0 ? audioDuration : recordingTime;
        }, [audioDuration, currentTime, isActive, isPlaybackMode, playbackDuration, recordingTime]);

        // ========================================
        // EVENT HANDLERS & CALLBACKS
        // ========================================
        const handlePlayPause = useCallback(() => {
            onPlayPause?.();
        }, [onPlayPause]);

        /**
         * Get mouse/touch position relative to canvas
         */
        const getPositionFromEvent = useCallback(
            (event) => {
                const canvas = canvasRef.current;
                if (!canvas) return null;

                const rect = canvas.getBoundingClientRect();
                const clientX = event.touches ? event.touches[0].clientX : event.clientX;
                const x = clientX - rect.left;
                return Math.max(0, Math.min(x, canvasWidth));
            },
            [canvasWidth],
        );

        /**
         * Calculate time from canvas X position
         * Accounts for 6px padding on each side
         */
        const getTimeFromPosition = useCallback(
            (x) => {
                const duration = isPlaybackMode ? playbackDuration : audioDuration;
                if (duration <= 0 || canvasWidth <= 0) return 0;
                const SCRUBBER_PADDING = 6;
                const availableWidth = canvasWidth - SCRUBBER_PADDING * 2;
                // Clamp x to the padded area
                const clampedX = Math.max(
                    SCRUBBER_PADDING,
                    Math.min(x, canvasWidth - SCRUBBER_PADDING),
                );
                // Map from padded position to time
                const normalizedPosition = (clampedX - SCRUBBER_PADDING) / availableWidth;
                return normalizedPosition * duration;
            },
            [audioDuration, canvasWidth, isPlaybackMode, playbackDuration],
        );

        /**
         * Calculate scrubber position aligned with waveform bar boundaries
         * This ensures the scrubber aligns with the progress visualization
         */
        const getScrubberPosition = useCallback(
            (time, isDraggingMode, dragPos) => {
                const SCRUBBER_PADDING = 6;
                const availableWidth = canvasWidth - SCRUBBER_PADDING * 2;

                if (isDraggingMode) {
                    // When dragging, clamp dragPosition to padded area
                    return Math.max(
                        SCRUBBER_PADDING,
                        Math.min(dragPos, canvasWidth - SCRUBBER_PADDING),
                    );
                }

                // Calculate position aligned with waveform bars
                // Use same logic as drawStaticWaveform for consistency
                const barCount = 100; // Default waveformBars from useAudioEvents
                const barGap = 2;
                const totalGapWidth = barGap * (barCount - 1);
                const barWidth = Math.max(2, (availableWidth - totalGapWidth) / barCount);

                // Calculate progress and current bar
                const progress = playbackDuration > 0 ? time / playbackDuration : 0;
                const playedBarCount = Math.floor(progress * barCount);

                // Position scrubber at the right edge of the last played bar
                // This matches where the waveform visual progress ends (bars 0 to playedBarCount-1 are colored)
                // Use smooth interpolation within the current bar for smoother movement
                const fractionInBar = progress * barCount - playedBarCount;
                const scrubberPosition =
                    SCRUBBER_PADDING +
                    playedBarCount * (barWidth + barGap) +
                    fractionInBar * barWidth;

                // Clamp to padded area
                return Math.max(
                    SCRUBBER_PADDING,
                    Math.min(scrubberPosition, canvasWidth - SCRUBBER_PADDING),
                );
            },
            [canvasWidth, playbackDuration],
        );

        /**
         * Handle drag start
         */
        const handleDragStart = useCallback(
            (event) => {
                if (!isPlaybackMode || playbackDuration <= 0) return;

                event.preventDefault();
                const position = getPositionFromEvent(event);
                if (position === null) return;

                // Store if audio was playing when drag started
                wasPlayingRef.current = isPlaying;

                // Pause playback if playing
                if (isPlaying) {
                    onPlayPause?.();
                }

                // Start drag
                setIsDragging(true);
                setDragPosition(position);

                // Update current time immediately for visual feedback
                const targetTime = getTimeFromPosition(position);
                dispatch({ type: ACTIONS.SET_CURRENT_TIME, payload: targetTime });
            },
            [
                getPositionFromEvent,
                getTimeFromPosition,
                isPlaybackMode,
                isPlaying,
                onPlayPause,
                playbackDuration,
            ],
        );

        /**
         * Handle drag move
         */
        const handleDragMove = useCallback(
            (event) => {
                if (!isDragging) return;

                event.preventDefault();
                const position = getPositionFromEvent(event);
                if (position === null) return;

                setDragPosition(position);

                // Update current time for visual feedback
                const targetTime = getTimeFromPosition(position);
                dispatch({ type: ACTIONS.SET_CURRENT_TIME, payload: targetTime });
            },
            [getPositionFromEvent, getTimeFromPosition, isDragging],
        );

        /**
         * Handle drag end
         */
        const handleDragEnd = useCallback(
            (event) => {
                if (!isDragging) return;

                event.preventDefault();
                const position = getPositionFromEvent(event);
                if (position === null) {
                    setIsDragging(false);
                    return;
                }

                // Calculate target time
                const targetTime = getTimeFromPosition(position);

                // Seek to position
                if (onSeek) {
                    onSeek(targetTime, wasPlayingRef.current);
                }

                // Reset drag state
                setIsDragging(false);
                setDragPosition(0);
            },
            [getPositionFromEvent, getTimeFromPosition, isDragging, onSeek],
        );

        /**
         * Handle mouse/touch leave canvas
         */
        const handleDragCancel = useCallback(() => {
            if (!isDragging) return;

            // If drag was cancelled, seek to last known position
            const targetTime = getTimeFromPosition(dragPosition);
            if (onSeek) {
                onSeek(targetTime, wasPlayingRef.current);
            }

            setIsDragging(false);
            setDragPosition(0);
        }, [dragPosition, getTimeFromPosition, isDragging, onSeek]);

        // ========================================
        // EFFECTS
        // ========================================
        // Observe canvas wrapper size changes for responsive canvas
        useEffect(() => {
            if (!canvasWrapperRef.current || width) return; // Skip if width is explicitly set

            const updateCanvasWidth = (targetWidth) => {
                if (targetWidth > 0) {
                    const width = Math.floor(targetWidth);
                    dispatch({ type: ACTIONS.SET_CANVAS_WIDTH, payload: width });
                    onResize?.(width, height);

                    // Immediately update canvas element width to match wrapper
                    const canvas = canvasRef.current;
                    if (canvas && canvas.width !== width) {
                        canvas.width = width;
                    }
                }
            };

            const updateInitialWidth = () => {
                if (!canvasWrapperRef.current) return;

                // Get initial width from the wrapper element after layout is complete
                const initialWidth = canvasWrapperRef.current.getBoundingClientRect().width;
                updateCanvasWidth(initialWidth);
            };

            // Use requestAnimationFrame to ensure layout is complete
            const rafId = requestAnimationFrame(updateInitialWidth);

            const resizeObserver = new ResizeObserver((entries) => {
                for (const entry of entries) {
                    // Use the actual computed width of the canvas wrapper (after flex layout)
                    const newWidth = entry.contentRect.width;
                    updateCanvasWidth(newWidth);
                }
            });

            resizeObserver.observe(canvasWrapperRef.current);
            // Also observe the container to catch changes when play button appears/disappears
            if (containerRef.current) {
                resizeObserver.observe(containerRef.current);
            }
            resizeObserverRef.current = resizeObserver;

            return () => {
                cancelAnimationFrame(rafId);
                resizeObserver.disconnect();
                resizeObserverRef.current = null;
            };
        }, [height, onResize, width]);

        // Update canvas width when width prop changes
        useEffect(() => {
            if (width) {
                dispatch({ type: ACTIONS.SET_CANVAS_WIDTH, payload: width });
            }
        }, [width]);

        // Redraw when canvas width changes in playback mode or when recording state changes
        useEffect(() => {
            // Check if width actually changed
            const widthChanged = prevCanvasWidthRef.current !== canvasWidth;
            prevCanvasWidthRef.current = canvasWidth;

            if (isPlaybackMode && playbackDuration > 0 && canvasWidth > 0 && widthChanged) {
                // Trigger redraw when canvas is resized
                const SCRUBBER_PADDING = 6;
                onTimeUpdate?.(currentTime, playbackDuration, SCRUBBER_PADDING);
            }
        }, [canvasWidth, currentTime, isPlaybackMode, isPlaying, onTimeUpdate, playbackDuration]);

        // Force resize check when layout-affecting props change
        // This handles cases where play button visibility changes affect available width
        useEffect(() => {
            if (!canvasWrapperRef.current || width) return; // Skip if width is explicitly set

            // Force a resize check when recording state or playback mode changes
            // This ensures the canvas resizes when play button appears/disappears
            const checkAndUpdateCanvasSize = () => {
                if (!canvasWrapperRef.current) return;

                const wrapper = canvasWrapperRef.current;
                const currentWidth = wrapper.getBoundingClientRect().width;
                const canvas = canvasRef.current;

                if (currentWidth > 0) {
                    const newWidth = Math.floor(currentWidth);

                    // Always update canvas element width to match wrapper
                    if (canvas && canvas.width !== newWidth) {
                        canvas.width = newWidth;
                    }

                    // Update state if width changed
                    if (newWidth !== canvasWidth && newWidth > 0) {
                        dispatch({ type: ACTIONS.SET_CANVAS_WIDTH, payload: newWidth });
                        onResize?.(newWidth, height);
                    }
                }
            };

            // Use multiple requestAnimationFrame calls to ensure layout is complete after DOM changes
            // The play button appearing/disappearing changes the flex layout, so we need to wait for reflow
            let rafId1 = null;
            let rafId2 = null;
            let timeoutId = null;

            rafId1 = requestAnimationFrame(() => {
                rafId2 = requestAnimationFrame(() => {
                    checkAndUpdateCanvasSize();
                });
            });

            // Also use setTimeout as a fallback to ensure it happens even if RAF is delayed
            timeoutId = setTimeout(() => {
                checkAndUpdateCanvasSize();
            }, 100);

            return () => {
                if (rafId1) cancelAnimationFrame(rafId1);
                if (rafId2) cancelAnimationFrame(rafId2);
                if (timeoutId) clearTimeout(timeoutId);
            };
        }, [canvasWidth, height, isActive, isPlaybackMode, onResize, width]);

        // Update current time during playback
        useEffect(() => {
            if (isPlaying && playbackContext && isPlaybackMode) {
                const SCRUBBER_PADDING = 6;
                const updateTime = () => {
                    const elapsed = playbackContext.currentTime - playbackStartTime;
                    const newTime = Math.min(elapsed, playbackDuration);
                    dispatch({ type: ACTIONS.SET_CURRENT_TIME, payload: newTime });

                    // Notify parent of time update
                    onTimeUpdate?.(newTime, playbackDuration, SCRUBBER_PADDING);

                    // Check if audio has reached the end
                    if (newTime >= playbackDuration && playbackDuration > 0) {
                        // Pause playback and reset to beginning when end is reached
                        dispatch({ type: ACTIONS.RESET_CURRENT_TIME });
                        onPlayPause?.();
                        return;
                    }

                    if (elapsed < playbackDuration) {
                        playbackAnimationRef.current = requestAnimationFrame(updateTime);
                    }
                };

                playbackAnimationRef.current = requestAnimationFrame(updateTime);

                return () => {
                    if (playbackAnimationRef.current) {
                        cancelAnimationFrame(playbackAnimationRef.current);
                        playbackAnimationRef.current = null;
                    }
                };
            }
        }, [
            isPlaybackMode,
            isPlaying,
            onPlayPause,
            onTimeUpdate,
            playbackContext,
            playbackDuration,
            playbackStartTime,
        ]);

        // Reset recording time when not recording
        useEffect(() => {
            if (!isActive && !isPlaybackMode && audioDuration === 0) {
                dispatch({ type: ACTIONS.RESET_RECORDING_TIME });
            }
        }, [audioDuration, isActive, isPlaybackMode]);

        // Track wall-clock elapsed time during recording
        // This provides continuous visual feedback regardless of VAD/speech detection
        useEffect(() => {
            // Only track time when actively recording (not in playback mode)
            if (isActive && !isPlaybackMode) {
                // Capture the precise moment recording begins - this happens immediately
                // when the effect runs, before any animation frames
                const startTime = recordingStartTimeRef.current ?? performance.now();
                recordingStartTimeRef.current = startTime;

                // Set initial recording time immediately (don't wait for first frame)
                dispatch({ type: ACTIONS.SET_RECORDING_TIME, payload: 0 });

                const updateRecordingTime = () => {
                    const elapsed = (performance.now() - startTime) / 1000;
                    dispatch({ type: ACTIONS.SET_RECORDING_TIME, payload: elapsed });
                    recordingAnimationRef.current = requestAnimationFrame(updateRecordingTime);
                };

                // Start the animation loop
                recordingAnimationRef.current = requestAnimationFrame(updateRecordingTime);

                return () => {
                    if (recordingAnimationRef.current) {
                        cancelAnimationFrame(recordingAnimationRef.current);
                        recordingAnimationRef.current = null;
                    }
                };
            } else {
                // Reset start time when not recording
                recordingStartTimeRef.current = null;
            }
        }, [isActive, isPlaybackMode]);

        // Attach event listeners to canvas for drag functionality
        useEffect(() => {
            const canvas = canvasRef.current;
            if (!canvas || !isPlaybackMode || playbackDuration <= 0) return;

            // Mouse events
            canvas.addEventListener('mousedown', handleDragStart);
            canvas.addEventListener('mousemove', handleDragMove);
            canvas.addEventListener('mouseup', handleDragEnd);
            canvas.addEventListener('mouseleave', handleDragCancel);

            // Touch events
            canvas.addEventListener('touchstart', handleDragStart, { passive: false });
            canvas.addEventListener('touchmove', handleDragMove, { passive: false });
            canvas.addEventListener('touchend', handleDragEnd);
            canvas.addEventListener('touchcancel', handleDragCancel);

            // Set cursor style for playback mode
            canvas.style.cursor = 'pointer';

            return () => {
                canvas.removeEventListener('mousedown', handleDragStart);
                canvas.removeEventListener('mousemove', handleDragMove);
                canvas.removeEventListener('mouseup', handleDragEnd);
                canvas.removeEventListener('mouseleave', handleDragCancel);
                canvas.removeEventListener('touchstart', handleDragStart);
                canvas.removeEventListener('touchmove', handleDragMove);
                canvas.removeEventListener('touchend', handleDragEnd);
                canvas.removeEventListener('touchcancel', handleDragCancel);
            };
        }, [
            handleDragCancel,
            handleDragEnd,
            handleDragMove,
            handleDragStart,
            isPlaybackMode,
            playbackDuration,
        ]);

        // Draw scrubber on canvas continuously
        useEffect(() => {
            const canvas = canvasRef.current;
            if (!canvas || !isPlaybackMode || playbackDuration <= 0) return;

            const drawScrubber = () => {
                const ctx = canvas.getContext('2d');
                if (!ctx) return;

                const SCRUBBER_PADDING = 6;

                // Calculate scrubber position aligned with waveform bar boundaries
                const scrubberPosition = getScrubberPosition(
                    isDragging ? getTimeFromPosition(dragPosition) : currentTime,
                    isDragging,
                    dragPosition,
                );

                // Erase previous scrubber position every frame to prevent trails
                // Use destination-out composite to erase only the exact circle area
                if (prevScrubberPositionRef.current !== null) {
                    // Erase previous scrubber circle using destination-out composite
                    ctx.save();
                    ctx.globalCompositeOperation = 'destination-out';
                    ctx.beginPath();
                    ctx.arc(prevScrubberPositionRef.current, height / 2, 4, 0, Math.PI * 2);
                    ctx.fill();
                    ctx.restore();
                }

                // Check if position changed significantly
                const positionChanged =
                    prevScrubberPositionRef.current === null ||
                    Math.abs(prevScrubberPositionRef.current - scrubberPosition) > 0.5;

                // Redraw waveform when position changes to restore erased area and show progress
                if (positionChanged && onTimeUpdate) {
                    const displayTime = isDragging
                        ? getTimeFromPosition(dragPosition)
                        : currentTime;
                    // Pass padding to waveform drawing
                    onTimeUpdate(displayTime, playbackDuration, SCRUBBER_PADDING);
                }

                // Draw scrubber circle at new position
                ctx.save();
                // Enable antialiasing for smooth rendering
                ctx.imageSmoothingEnabled = true;
                ctx.imageSmoothingQuality = 'high';
                ctx.fillStyle = getVar(theme.colors.primary.black);
                ctx.beginPath();
                ctx.arc(scrubberPosition, height / 2, 4, 0, Math.PI * 2);
                ctx.fill();
                ctx.restore();

                // Store current position for next frame
                prevScrubberPositionRef.current = scrubberPosition;

                // Continue animation loop
                scrubberAnimationRef.current = requestAnimationFrame(drawScrubber);
            };

            // Start animation loop
            scrubberAnimationRef.current = requestAnimationFrame(drawScrubber);

            return () => {
                if (scrubberAnimationRef.current) {
                    cancelAnimationFrame(scrubberAnimationRef.current);
                    scrubberAnimationRef.current = null;
                }
                // Reset previous position on cleanup
                prevScrubberPositionRef.current = null;
            };
        }, [
            canvasWidth,
            currentTime,
            dragPosition,
            getScrubberPosition,
            getTimeFromPosition,
            height,
            isDragging,
            isPlaybackMode,
            onTimeUpdate,
            playbackDuration,
            theme.colors.actionMajor,
            theme.colors.primary.black,
        ]);

        // ========================================
        // RENDER
        // ========================================
        return (
            <div ref={containerRef} className={classNames(classes.root, className)}>
                {isPlaybackMode && (
                    <span className={classes.playButton}>
                        <Icon
                            name={isPlaying ? 'pause' : 'play'}
                            size="medium"
                            color={theme.colors.actionMajor[500]}
                            onClick={handlePlayPause}
                        />
                    </span>
                )}
                <div ref={canvasWrapperRef} className={classes.canvasWrapper}>
                    <canvas
                        ref={(el) => {
                            canvasRef.current = el;
                            bindCanvas(el, height);
                        }}
                        className={classNames(classes.canvas, {
                            [classes.canvasActive]: isActive && level > 0.01,
                        })}
                        height={height}
                        width={canvasWidth}
                    />
                </div>
                <span className={classes.timeIndicator}>{formatTime(displayTime)}</span>
            </div>
        );
    },
);

AudioVisualizer.propTypes = {
    audioBufferDuration: PropTypes.number,
    audioDuration: PropTypes.number,
    bindCanvas: PropTypes.func.isRequired,
    className: PropTypes.string,
    height: PropTypes.number,
    isActive: PropTypes.bool,
    isPlaybackMode: PropTypes.bool,
    isPlaying: PropTypes.bool,
    level: PropTypes.number,
    onPlayPause: PropTypes.func,
    onResize: PropTypes.func,
    onSeek: PropTypes.func,
    onTimeUpdate: PropTypes.func,
    playbackContext: PropTypes.object,
    playbackStartTime: PropTypes.number,
    width: PropTypes.number,
};

export default AudioVisualizer;
